import React, { useCallback, useEffect, useState } from "react";
import { PageLoading } from "../../../common/components/Loading";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { NetworkRequest } from "../../../common/models";
import { Api } from "../../../common/service/Api";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { usePrimaryOrganization } from "../../hooks";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { buildStepsFromWorkflowType } from "./HorizontalStepProgressIndicator";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { StepNumberCircle } from "@truevault/aquilo";
import { CustomMessageSampleDto } from "../../../common/service/server/dto/CustomMessageSampleDto";
import { RequestHandlingInstructionsTextarea } from "./RequestHandlingInstructionsTextarea";

export const ProfilngOptOutInstructionsPage = () => {
  // Constants

  // State
  const [readyToRender, setReadyToRender] = useState(false);
  const [instructions, setInstructions] = useState(null);

  // Requests
  const [organization, orgRequest] = usePrimaryOrganization();

  const fetchedInstructionsApi = useCallback(
    () => Api.requestHandlingInstructions.getProfilingOptOutInstructions(organization.id),
    [organization.id],
  );
  const {
    result: fetchedInstructions,
    refresh: refreshInstruction,
    invalidate: invalidateInstructions,
    request: instructionsRequest,
    isSettled,
  } = useDataRequest({
    queryKey: ["instructions", organization.id],
    api: fetchedInstructionsApi,
  });

  const updateInstructionsApi = useCallback(
    (newInstructions?: string) =>
      Api.requestHandlingInstructions.updateProfilingOptOutInstructions({
        organizationId: organization.id,
        requestHandlingInstructions: newInstructions ?? instructions,
      }),
    [organization.id, instructions],
  );
  const { fetch: updateInstructions } = useActionRequest({
    api: updateInstructionsApi,
    onSuccess: () => {
      invalidateInstructions();
      refreshInstruction();
    },
  });

  const {
    result: consumerRequestClosedAutoProfilingSampleMessage,
    refresh: refreshConsumerMessage,
  } = useDataRequest({
    queryKey: ["emailTemplate", organization?.id, "closed-auto-profiling"],
    api: () =>
      Api.organization.getOrganizationMessageSample(organization?.id, "closed-auto-profiling"),
  });

  const showCustomTextInputField =
    consumerRequestClosedAutoProfilingSampleMessage?.templateHTML?.includes("[[[CUSTOM_TEXT]]]");

  // Effects
  useEffect(() => {
    if (NetworkRequest.areFinished(orgRequest)) {
      setReadyToRender(true);
    }
  }, [orgRequest]);

  useEffect(() => {
    if (instructionsRequest.success && instructions === null && isSettled) {
      setInstructions(fetchedInstructions);
    }
  }, [fetchedInstructions, instructionsRequest, instructions, isSettled]);

  // Content

  if (!readyToRender || !organization) {
    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  if (!organization.requiresProfilingOptOut) {
    return (
      <WorkflowSetupPageWrapper
        selectedTabKey="request-to-profiling-opt-out"
        nextOnClick={async () => {}}
        nextDisabled={false}
      >
        <div className="tw-boundary">
          <div className="text-neutral-600 font-semibold mt-12 max-w-[776px]">
            Based on your data map you do not require a profiling opt-out workflow.
          </div>
        </div>
      </WorkflowSetupPageWrapper>
    );
  }

  // Use shared workflow steps - current step is "stop auto profiling" (index 1) for instructions config
  const profilingStepProgressSteps = buildStepsFromWorkflowType(WorkflowType.PROFILING_OPT_OUT, 1);

  const handleStepProgressClick = (stepId: string) => {
    document.getElementById(stepId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="request-to-profiling-opt-out"
      nextOnClick={async () => {
        await updateInstructions();
      }}
      nextDisabled={false}
      stepProgressSteps={profilingStepProgressSteps}
      onStepProgressClick={handleStepProgressClick}
    >
      <>
        <div id="verify-consumer" />

        <div id="stop-auto-profiling" className="tw-boundary">
          <div className="flex items-center gap-3 mt-10">
            <StepNumberCircle text="1" />
            <div className="text-neutral-900 text-[18px] font-normal">
              Discontinue use of consumer&apos;s personal data for automated profiling
            </div>
          </div>
          <div className="text-neutral-900 text-[14px] ml-[40px] mt-[6px]">
            (Optional) Add additional instructions
          </div>
          <div className="ml-[40px] mt-[6px] max-w-[736px]">
            <RequestHandlingInstructionsTextarea
              rows={6}
              className="block w-full rounded-md border border-solid border-[#D8CEF8] bg-white px-3 py-2 text-[18px] text-neutral-800 placeholder:text-neutral-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
              value={instructions ?? ""}
              placeholder="E.g. List all the apps that are used for automated profiling"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setInstructions(e.target.value)
              }
              onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
                if (e.target.value !== fetchedInstructions) {
                  await updateInstructions(e.target.value);
                }
              }}
            />
          </div>
        </div>

        <div id="notify-consumer" className="tw-boundary">
          <div className="flex items-center gap-3 mt-10">
            <StepNumberCircle text="2" />
            <div className="text-gray-900 text-[18px] font-normal">Notify consumer</div>
          </div>
          <div className="text-neutral-900 text-[14px] ml-[40px] mt-[12px]">
            Consumer Email Notification Template (auto-profiling stopped)
          </div>
        </div>

        <div style={{ marginTop: "12px", marginLeft: "40px", marginBottom: "64px" }}>
          <div
            style={{
              backgroundColor: "#F9F8FB",
              padding: "32px",
              maxWidth: "736px",
              marginTop: "6px",
            }}
          >
            <p>
              <strong>To:</strong> <span>consumer@example.com</span>
            </p>
            <p>
              <strong>Subject: A message about your request [REQUEST ID#]</strong>
            </p>

            {showCustomTextInputField ? (
              <ProfilingEmailTemplateWithCustomTextInputField
                orgId={organization.id}
                consumerRequestSampleMessage={consumerRequestClosedAutoProfilingSampleMessage}
                refreshConsumerMessage={refreshConsumerMessage}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: consumerRequestClosedAutoProfilingSampleMessage?.templateHTML,
                }}
              />
            )}
          </div>
        </div>
      </>
    </WorkflowSetupPageWrapper>
  );
};

function ProfilingEmailTemplateWithCustomTextInputField({
  orgId,
  refreshConsumerMessage,
  consumerRequestSampleMessage,
}: {
  orgId: string;
  refreshConsumerMessage: () => Promise<void>;
  consumerRequestSampleMessage: CustomMessageSampleDto;
}) {
  const { fetch: updateCustomText } = useActionRequest({
    api: (customMessage) =>
      Api.organization.updateOrganizationMessageSample(
        orgId,
        "closed-auto-profiling",
        customMessage?.trim() ?? "",
      ),
  });

  const [customText, setCustomText] = useState(consumerRequestSampleMessage.customText);
  const [beforeHTML, afterHTML] = consumerRequestSampleMessage.templateHTML
    .split(`<p class="custom-text">[[[CUSTOM_TEXT]]]</p>`)
    .map((t) => t.replace(/href/gi, "xhref"));

  return (
    <div>
      <div dangerouslySetInnerHTML={{ __html: beforeHTML }} />
      <div className="tw-boundary">
        <textarea
          rows={4}
          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          defaultValue={customText.length > 0 ? customText : null}
          placeholder="(optional) Add your own language here"
          onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
            const newCustomText = e.target.value;
            setCustomText(newCustomText);
            await updateCustomText(newCustomText);
            await refreshConsumerMessage();
          }}
        />
      </div>
      <div dangerouslySetInnerHTML={{ __html: afterHTML }} />
    </div>
  );
}
